<template>
  <t-select
    :value="modelValue"
    :popup-visible="popupVisible"
    :placeholder="$t('选择变量')"
    @popupVisibleChange="onPopupVisibleChange"
    @change="handleChange"
  >
    <!-- 自定义变量选项（没有传递 infosOptions 时显示） -->
    <template v-if="!infosOptions || infosOptions.length === 0">
      <t-option
        v-for="(option, index) in optionsData"
        :key="option.value"
        :value="option.value"
        :label="option.label"
        class="custom-option"
        @mouseenter="showButton(option)"
        @mouseleave="hideButton(option)"
      >
        <t-tooltip :content="option.tip">
          <div>{{ option.label }}</div>
        </t-tooltip>
        <div v-if="option.showButton">
          <img
            :src="EditNodeIcon"
            class="custom-button"
            @click="handleButtonClick({ option, index, type: 'edit' }, $event)"
          />
          <img
            :src="DeleteNodeIcon"
            class="custom-button"
            @click="
              handleButtonClick({ option, index, type: 'delete' }, $event)
            "
          />
        </div>
      </t-option>
    </template>
    <!-- 信息提取节点选项（传递了 infosOptions 时显示） -->
    <t-option
      v-for="option in infosOptions"
      :key="option.value"
      :value="option.value"
      :label="option.label"
      class="custom-option"
    >
      <t-tooltip :content="option.tip">
        <div>{{ option.label }}</div>
      </t-tooltip>
    </t-option>
    <t-option
      v-if="!props.hideCreateButton"
      @click="
        handleButtonClick({ option: {}, index: -1, type: 'create' }, $event)
      "
    >
      <span style="padding: 0px; color: #0052d9">
        {{ $t("新建变量") }}
      </span>
    </t-option>
  </t-select>
  <t-dialog
    v-model:visible="visible"
    width="300"
    :header="false"
    class="variable-dialog"
    @confirm="handleConfirm"
  >
    <p v-if="variable.type === 'delete'">{{ $t("确认删除？") }}</p>
    <div v-else>
      <t-input
        v-model="variable.value"
        :placeholder="$t('请输入变量key')"
        :status="errorMessage ? 'error' : 'default'"
        maxlength="100"
        @input="handleInput"
      />
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </t-dialog>
</template>

<script lang="ts" setup>
import { ref, Ref, reactive, inject, watch } from "vue";
import { cloneDeep, map, trim, concat, find } from "lodash-es";
import EditNodeIcon from "@/assets/icons/edit-node-icon.svg";
import DeleteNodeIcon from "@/assets/icons/delete-node-icon.svg";
import { systemVarsPool } from "@/utils/const";
import { TaskType } from "@/main2";
import { t } from "@/utils/i18n";

const taskType = inject<TaskType>("taskType");
const variablePool = inject<Ref<string[]>>("variablePool", ref<string[]>([]));
const changedVar = inject<Ref<{ oldValue: string; newValue: string }>>(
  "changedVar",
  ref<{ oldValue: string; newValue: string }>({ oldValue: "", newValue: "" }),
);
const updateVariablePool = inject<
  (
    v: string[],
    extra?: {
      index: number;
      type: string;
      oldValue: string;
      newValue: string;
    },
  ) => void
>("updateVariablePool");

const props = defineProps<{
  modelValue: string;
  withSystem?: boolean;
  infosOptions?: {
    value: string;
    label: string;
    tip: string;
  }[];
  hideCreateButton?: boolean; // 是否隐藏"新建变量"按钮
}>();

const optionsData = ref(
  map(variablePool.value, (option) => ({
    value: option,
    label: option,
    tip: "",
    showButton: false,
  })),
);

const _systemVarsPool = map(systemVarsPool, (v) => ({
  ...v,
  showButton: false,
  isSystem: true,
}));

const visible = ref(false);
const popupVisible = ref(false);
const variable = reactive({ value: "", type: "", index: -1 });
const errorMessage = ref("");

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
}>();

const onPopupVisibleChange = (v) => {
  popupVisible.value = v;
};

const handleChange = (value: string) => {
  emit("update:modelValue", value);
  emit("change", value);
};

const handleInput = () => {
  // 用户输入时清除错误信息
  errorMessage.value = "";
};

const handleConfirm = () => {
  const _arr = cloneDeep(variablePool.value);
  const { value, type, index } = variable;
  if (!trim(value) && type !== "delete") return;
  const trimmedValue = trim(value);

  // 检查变量名称是否与系统变量重复
  const isSystemVarName = systemVarsPool.some(
    (sysVar) => sysVar.value === trimmedValue,
  );

  // 如果与系统变量重复，则提示用户
  if (isSystemVarName) {
    errorMessage.value = t("该变量名称已存在，请尝试其他名称");
    return;
  }

  // 清除错误信息
  errorMessage.value = "";

  if (type === "create") {
    _arr.push(trimmedValue);
  }
  if (type === "delete") {
    _arr.splice(index, 1);
  }
  if (type === "edit") {
    _arr[index] = trimmedValue;
  }
  updateVariablePool?.(_arr, {
    index,
    type,
    oldValue: variablePool.value[index],
    newValue: type === "delete" ? "" : value,
  });
  // 新建变量后自动选中新创建的变量
  if (type === "create") {
    handleChange(trimmedValue);
  }
  visible.value = false;
};

const showButton = (option) => {
  if (option.isSystem) return;
  option.showButton = true;
};

const hideButton = (option) => {
  option.showButton = false;
};

const handleButtonClick = ({ option, type, index }, e) => {
  e.preventDefault();
  e.stopPropagation();
  popupVisible.value = false;
  visible.value = true;
  variable.value = type !== "create" ? option.label : "";
  variable.type = type;
  variable.index = index;
  // 重置错误信息
  errorMessage.value = "";
};

watch(
  [variablePool, () => props.withSystem],
  ([newVal, newA]) => {
    const arr = map(newVal, (option) => ({
      value: option,
      label: option,
      tip: "",
      showButton: false,
    }));
    if (newA) {
      optionsData.value = concat(arr, _systemVarsPool);
    } else {
      optionsData.value = cloneDeep(arr);
    }
  },
  { immediate: true },
);

watch(
  changedVar,
  (newVal) => {
    if (newVal.oldValue === props.modelValue) {
      handleChange(newVal.newValue);
    }
  },
  { immediate: true },
);
</script>

<style lang="less" scoped>
:global(.custom-option span) {
  display: flex !important;
  width: 100%;
  justify-content: space-between;
}

.custom-button {
  vertical-align: middle;
  cursor: pointer;
  margin-left: 5px;
}

.error-message {
  color: var(--td-error-color);
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.5;
}
</style>
